import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi, setToken } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import Loading from "@/components/app/Loading";
import { IDS } from "@/constants/testIds";
import { toast } from "sonner";

export default function Callback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [status, setStatus] = useState("Authenticating...");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    (async () => {
      // session_id can arrive in the hash fragment (Emergent Auth default) or as query.
      let sessionId = null;
      const hash = window.location.hash || "";
      const hm = hash.match(/session_id=([^&]+)/);
      if (hm) sessionId = decodeURIComponent(hm[1]);
      if (!sessionId) {
        const qs = new URLSearchParams(window.location.search);
        sessionId = qs.get("session_id");
      }

      if (!sessionId) {
        setError("No session_id received. Please try signing in again.");
        setTimeout(() => navigate("/login", { replace: true }), 1500);
        return;
      }

      try {
        const res = await authApi.exchangeSession(sessionId);
        if (res.success && res.user) {
          // Backend returns session_token in body (also sets HttpOnly cookie).
          const token = res.session_token || res.user?.session_token;
          if (token) setToken(token);
          // Auto-granted purchases at sign-in (Wix email-match safety net) — surface
          // a toast so the customer sees their purchase landed.
          const granted = res.auto_granted_products || [];
          if (granted.length) {
            const label = granted.length === 1
              ? `Your purchase is now in your Library`
              : `${granted.length} purchases were added to your Library`;
            toast.success(label);
          }
          setStatus("Signed in. Redirecting...");
          await refresh();
          navigate("/dashboard", { replace: true });
        } else {
          throw new Error("Authentication failed");
        }
      } catch (e) {
        setError(e.message || "Authentication failed");
        setTimeout(() => navigate("/login", { replace: true }), 2000);
      }
    })();
  }, [navigate, refresh]);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center gap-4"
      style={{ background: "var(--pa-background)" }}
      data-testid={IDS.callbackPage}
    >
      {error ? (
        <div className="text-center px-6">
          <p className="pa-serif text-xl mb-2" style={{ color: "var(--pa-error)" }}>Sign-in failed</p>
          <p className="text-sm" style={{ color: "var(--pa-text-secondary)" }}>{error}</p>
        </div>
      ) : (
        <>
          <Loading label={status} />
        </>
      )}
    </div>
  );
}
